import { mean } from 'd3';
import { Vector3 } from 'three';

import { GcpModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { isDefined } from '../../../../utils/variables';
import { GeodataMarkerWithEstimates } from './place-markers-state';

export function calculateGcpError(gcp: GcpModel, markers: GeodataMarkerWithEstimates[]) {
  const totalPinned = markers
    .filter((marker) => {
      if (marker.gcpId !== gcp.id) {
        return false;
      }

      const hasAcceptedEstimate = marker.estimates?.some(
        (estimate) => estimate.markerId === marker.id && estimate.status === 'ACCEPTED',
      );

      if (!hasAcceptedEstimate && !marker.pinned) {
        return false;
      }

      return true;
    })
    .map((marker) => marker.residualError);

  const totalErrors = totalPinned.filter(isDefined);
  const n = totalErrors.length;

  if (n === 0) {
    return {
      rmseEnu: undefined,
      biasEnu: undefined,
      sumSqEnu: undefined,
      optimizedCount: 0,
      pinnedCount: totalPinned.length,
    };
  }

  const biasX = mean(totalErrors.map((error) => error[0])) ?? 0;
  const biasY = mean(totalErrors.map((error) => error[1])) ?? 0;
  const biasZ = mean(totalErrors.map((error) => error[2])) ?? 0;

  // Sum of squares (needed for global RMSE)
  const sumSqX = totalErrors.reduce((acc, e) => acc + e[0] * e[0], 0);
  const sumSqY = totalErrors.reduce((acc, e) => acc + e[1] * e[1], 0);
  const sumSqZ = totalErrors.reduce((acc, e) => acc + e[2] * e[2], 0);

  // Per-GCP RMSE
  const rmseX = Math.sqrt(sumSqX / n);
  const rmseY = Math.sqrt(sumSqY / n);
  const rmseZ = Math.sqrt(sumSqZ / n);

  return {
    rmseEnu: new Vector3(rmseX, rmseY, rmseZ),
    biasEnu: new Vector3(biasX, biasY, biasZ),
    sumSqEnu: new Vector3(sumSqX, sumSqY, sumSqZ),
    optimizedCount: totalErrors.length,
    pinnedCount: totalPinned.length,
  };
}
